if (!customElements.get('sticky-nav')) {
  class StickyNav extends HTMLElement {
    constructor() {
      super();
      this.wrapper = this.closest('.section-sticky-nav');
      this.links = [...this.querySelectorAll('a')];
      this.scrollContainer = this.querySelector('[data-scroll]');
      this.scrollArrows = {
        left: this.querySelector('[data-scroll-left]'),
        right: this.querySelector('[data-scroll-right]')
      };
      this.tracker = this.querySelector('[data-tracker]');
      this.pageHeader = document.querySelector('sticky-header');
      this.scrollAmount = 140;
      // Land a hair past the target so sub-pixel rounding never leaves a
      // hairline of the previous section under the nav (see scroll-navigation.js).
      this.scrollNudge = 2;
      this.fixedHeight = 0;
      this.sections = this.links.map(link => this.targetFor(link));

      this.bindMethods();
    }

    // Links name their target by Scroll ID, the same data attribute the PDP
    // scroll navigation and product badges resolve. The leading '#' is only a
    // convention in the Anchor setting, so strip it before matching.
    targetFor(link) {
      const slug = (link.getAttribute('href') || '').replace(/^#/, '');
      return slug ? document.querySelector(`[data-scroll-target="${CSS.escape(slug)}"]`) : null;
    }

    bindMethods() {
      this.handleScroll = this.handleScroll.bind(this);
      this.handleClick = this.handleClick.bind(this);
      this.handleArrowClick = this.handleArrowClick.bind(this);
      this.updateScrollButtons = this.updateScrollButtons.bind(this);
      this.calculateTop = this.calculateTop.bind(this);
      this.measureFixedHeight = this.measureFixedHeight.bind(this);
    }

    connectedCallback() {
      this.measureFixedHeight();
      ['load', 'resize'].forEach(evt => window.addEventListener(evt, this.calculateTop));
      ['load', 'resize'].forEach(evt => window.addEventListener(evt, this.measureFixedHeight));
      window.addEventListener('scroll', this.handleScroll);
      this.links.forEach(link => link.addEventListener('click', this.handleClick));

      if (this.scrollContainer) {
        Object.entries(this.scrollArrows).forEach(([direction, arrow]) => {
          arrow?.addEventListener('click', () => this.handleArrowClick(direction === 'left' ? -this.scrollAmount : this.scrollAmount));
        });
        this.scrollContainer.addEventListener('scroll', this.updateScrollButtons);
        this.updateScrollButtons();
      }
    }

    handleScroll() {
      this.calculateTop();

      const spaceFromTop = (this.getBoundingClientRect().top + window.scrollY) - (this.pageHeader.offsetHeight - 1);

      this.classList.toggle('fixed', window.scrollY >= spaceFromTop);

      // Track the link by index rather than the section: with no id on the
      // target there is nothing to map a section back to its link, and several
      // sections can share one Scroll ID (e.g. the four Body care showcases).
      let activeIndex = -1;
      this.sections.forEach((section, index) => {
        if (!section) return;
        // -2px tolerance absorbs sub-pixel smooth-scroll landings so the link
        // still activates when the click lands a hair short of the threshold.
        if (window.scrollY >= this.absoluteTop(section) - this.additionalSpacing() - 2) activeIndex = index;
      });

      this.links.forEach(link => link.classList.remove('active'));
      if (activeIndex > -1) this.setActiveLink(this.links[activeIndex]);
    }

    setActiveLink(activeLink) {
      if (!activeLink) return;

      activeLink.classList.add('active');
      if (this.tracker) {
        Object.assign(this.tracker.style, {
          left: `${activeLink.offsetLeft}px`,
          minWidth: `${activeLink.offsetWidth}px`
        });
      }
    }

    absoluteTop(el) {
      return el.getBoundingClientRect().top + window.scrollY;
    }

    additionalSpacing() {
      // Use the nav's pinned height, not its live height: the nav is taller
      // before it gets the `fixed` class (CSS drops its padding when fixed), so
      // reading offsetHeight while unfixed would make the click target and the
      // active-link threshold disagree and the active link would not update.
      // No extra gap on top: sections are full-bleed, so any daylight under the
      // nav shows the tail of the previous one. Each section's own padding-top
      // supplies the breathing room.
      return this.pageHeader.offsetHeight + (this.fixedHeight || this.offsetHeight);
    }

    // Cache the nav's height in its fixed state so the measurement is stable
    // regardless of whether the nav is currently pinned.
    measureFixedHeight() {
      const wasFixed = this.classList.contains('fixed');
      if (!wasFixed) this.classList.add('fixed');
      this.fixedHeight = this.offsetHeight;
      if (!wasFixed) this.classList.remove('fixed');
    }

    calculateTop() {
      if (this.wrapper) {
        this.wrapper.style.top = `${this.pageHeader.offsetHeight - 1}px`;
      }
    }

    handleClick(event) {
      event.preventDefault();
      const targetSection = this.sections[this.links.indexOf(event.currentTarget)];
      if (targetSection) {
        window.scrollTo({
          top: Math.max(
            0,
            Math.ceil(this.absoluteTop(targetSection) - this.additionalSpacing()) +
              this.scrollNudge
          ),
          behavior: 'smooth'
        });
      }
    }

    handleArrowClick(amount) {
      this.scrollContainer?.scrollBy({ left: amount, behavior: 'smooth' });
    }

    updateScrollButtons() {
      if (!this.scrollContainer) return;
      const { scrollLeft, clientWidth, scrollWidth } = this.scrollContainer;
      this.scrollArrows.left?.classList.toggle('disabled', scrollLeft <= 0);
      this.scrollArrows.right?.classList.toggle('disabled', scrollLeft + clientWidth >= scrollWidth);
    }
  }

  customElements.define('sticky-nav', StickyNav);
}
